import { useMemo } from 'react';
import { Hash, Megaphone, MessagesSquare, Folder } from 'lucide-react';
import { Select } from '@/components/ui/Select';
import { Input } from '@/components/ui/Input';
import { Skeleton } from '@/components/ui/Skeleton';
import { useGuildResources } from '@/hooks/useGuildResources';
import type { GuildChannel } from '@/lib/api-client/guild-resources';

const TYPE_ICON: Record<GuildChannel['type'], typeof Hash> = {
  text: Hash,
  announcement: Megaphone,
  forum: MessagesSquare,
  category: Folder,
};

interface GuildChannelSelectProps {
  guildId: string | undefined;
  /** Discord channel id, or null/empty for "none selected". */
  value: string | null;
  onChange: (channelId: string | null) => void;
  /** Which channel types this field actually accepts — e.g. Tickets'
   * category field should only ever offer categories. Defaults to every
   * non-category text-like type (the common case: "post messages here"). */
  types?: GuildChannel['type'][];
  /** Whether "— None —" is a valid choice (optional fields like Welcome's
   * leave channel) vs. always required. */
  allowClear?: boolean;
  disabled?: boolean;
  placeholder?: string;
  'aria-label'?: string;
}

/**
 * Friendly Discord channel picker, backed by GET /api/guilds/:guildId/resources
 * (useGuildResources — guild-scoped, cached). Built on the project's
 * existing <Select> primitive (docs/DESIGN_SYSTEM.md §4.3 already
 * establishes native <select> as this design system's answer for
 * single-choice fields) rather than a new custom dropdown component.
 *
 * Falls back to a plain Discord-id text input — never removing the
 * field or losing a saved value — when live data can't be loaded
 * (bot offline, missing permissions, etc.), and always keeps a
 * currently-saved id selectable even if it no longer appears in the
 * live list (a deleted/inaccessible channel), labeled so that's obvious
 * rather than silently vanishing or getting wiped.
 */
export function GuildChannelSelect({
  guildId,
  value,
  onChange,
  types = ['text', 'announcement', 'forum'],
  allowClear = true,
  disabled,
  placeholder = 'Select a channel…',
  ...aria
}: GuildChannelSelectProps) {
  const { state } = useGuildResources(guildId);

  const grouped = useMemo(() => {
    if (state.kind !== 'ready') return null;
    const wanted = new Set(types);
    const relevant = state.data.channels.filter((ch) => wanted.has(ch.type));
    const byCategory = new Map<string, GuildChannel[]>();
    const uncategorized: GuildChannel[] = [];
    for (const ch of relevant) {
      if (ch.parentId) {
        const list = byCategory.get(ch.parentId) ?? [];
        list.push(ch);
        byCategory.set(ch.parentId, list);
      } else {
        uncategorized.push(ch);
      }
    }
    const categories = state.data.channels.filter((ch) => ch.type === 'category');
    return { categories, byCategory, uncategorized };
  }, [state, types]);

  if (state.kind === 'loading') {
    return <Skeleton className="h-9 w-full" aria-label="Loading channels…" />;
  }

  // Offline/forbidden/error: raw-ID fallback — the field stays fully
  // usable (and a previously saved id stays visible and editable) even
  // when Discord data can't be loaded, per the "do not silently destroy
  // an existing saved configuration" requirement.
  if (state.kind !== 'ready' || !grouped) {
    return (
      <div className="space-y-1">
        <Input
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value || null)}
          placeholder="Channel ID (Discord data unavailable)"
          inputMode="numeric"
          disabled={disabled}
          aria-label={aria['aria-label'] ?? 'Channel ID'}
        />
        <p className="text-micro text-text-tertiary">
          {state.kind === 'forbidden'
            ? "Discord data unavailable — you don't have permission to view this server's channels."
            : 'Discord data unavailable — enter the channel ID manually.'}
        </p>
      </div>
    );
  }

  const channels: GuildChannel[] = state.data.channels;
  const found: GuildChannel | undefined = value ? channels.find((ch) => ch.id === value) : undefined;
  const isUnknown = Boolean(value) && !found;
  const Icon = found ? TYPE_ICON[found.type] : Hash;

  return (
    <div className="space-y-1">
      <div className="relative">
        <Icon size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary z-10" aria-hidden="true" />
        <Select
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value || null)}
          disabled={disabled}
          className="pl-9"
          aria-label={aria['aria-label'] ?? 'Channel'}
        >
          {allowClear && <option value="">— None —</option>}
          {!allowClear && !value && <option value="" disabled>{placeholder}</option>}
          {isUnknown && <option value={value!}>Unknown channel ({value})</option>}
          {grouped.uncategorized.map((ch) => (
            <option key={ch.id} value={ch.id}>#{ch.name}</option>
          ))}
          {grouped.categories
            .filter((cat) => (grouped.byCategory.get(cat.id)?.length ?? 0) > 0)
            .map((cat) => (
              <optgroup key={cat.id} label={cat.name}>
                {grouped.byCategory.get(cat.id)!.map((ch) => (
                  <option key={ch.id} value={ch.id}>#{ch.name}</option>
                ))}
              </optgroup>
            ))}
        </Select>
      </div>
      {isUnknown && (
        <p className="text-micro text-warning">
          This channel no longer appears in the server — it may have been deleted or the bot may have lost access.
        </p>
      )}
    </div>
  );
}
